(() => {
  const sections = document.querySelectorAll(".fc-page");

  sections.forEach((section) => {
    const rows = Array.from(section.querySelectorAll(".fc-row[role='radio']"));
    const primary = section.querySelector("[data-fc-primary]");
    const secondary = section.querySelector("[data-fc-secondary]");
    const sticky = section.querySelector("[data-fc-sticky]");
    const stickyFull = section.querySelector("[data-fc-sticky-full]");
    const stickyShort = section.querySelector("[data-fc-sticky-short]");
    const cartBaseUrl = section.dataset.cartBaseUrl || "/cart/";
    const cartUrl = section.dataset.cartUrl || "/cart";
    const cartJsonUrl = section.dataset.cartJsonUrl || "/cart.js";
    const cartAddUrl = section.dataset.cartAddUrl || "/cart/add.js";
    const cartChangeUrl = section.dataset.cartChangeUrl || "/cart/change.js";
    const cartClearUrl = section.dataset.cartClearUrl || "/cart/clear.js";
    const checkoutUrl = section.dataset.checkoutUrl || "/checkout";
    const canonicalVariantId = section.dataset.canonicalVariantId || "";
    const canonicalVariantReady = section.dataset.canonicalVariantReady === "true";
    const selectedLine = section.querySelector("[data-fc-selected-line]");
    const selectedUnits = section.querySelector("[data-fc-selected-units]");
    const selectedTotal = section.querySelector("[data-fc-selected-total]");
    const selectedUnit = section.querySelector("[data-fc-selected-unit]");
    const selectedSavings = section.querySelector("[data-fc-selected-savings]");
    const stickyLine = section.querySelector("[data-fc-sticky-line]");
    const productImage = section.querySelector("[data-fc-product-image]");

    const availableRows = () => rows.filter((row) => row.dataset.available === "true");

    const selectedRow = () => (
      rows.find((row) => row.classList.contains("is-selected")) ||
      availableRows()[0] ||
      rows[0]
    );

    const selectedQuantity = (row) => {
      const quantity = Number(row?.dataset.checkoutQuantity || row?.dataset.selectedUnits || 0);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
    };

    const buildCartPermalink = (row) => {
      const quantity = selectedQuantity(row);
      if (!canonicalVariantId || !quantity || row?.dataset.available !== "true") return "";
      return `${cartBaseUrl}${canonicalVariantId}:${quantity}`;
    };

    const setButtonUrl = (button, checkoutPermalink) => {
      if (!button) return;

      button.toggleAttribute("aria-disabled", !checkoutPermalink);

      if (checkoutPermalink) {
        button.href = checkoutPermalink;
      } else {
        button.removeAttribute("href");
      }
    };

    const setButtonText = (button, text) => {
      if (!button) return;
      button.textContent = text;
    };

    const setActionText = (button, text) => {
      if (button === sticky && stickyFull && stickyShort && sticky.contains(stickyFull) && sticky.contains(stickyShort)) {
        stickyFull.textContent = text;
        stickyShort.textContent = text;
        return;
      }

      setButtonText(button, text);
    };

    const updateUrl = (row) => {
      if (!row || !row.dataset.selectedUnits || !window.history || !window.history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set("quantity", row.dataset.selectedUnits);
      url.searchParams.delete("variant");
      window.history.replaceState({}, "", url.toString());
    };

    const updateProductImage = (row) => {
      if (!productImage || !row || !row.dataset.imageSrc) return;

      if (productImage.getAttribute("src") !== row.dataset.imageSrc) {
        productImage.setAttribute("src", row.dataset.imageSrc);
      }

      if (row.dataset.imageAlt) {
        productImage.alt = row.dataset.imageAlt;
      }

      if (row.dataset.imageWidth) {
        productImage.setAttribute("width", row.dataset.imageWidth);
      }

      if (row.dataset.imageHeight) {
        productImage.setAttribute("height", row.dataset.imageHeight);
      }
    };

    const updateButtons = (row) => {
      const cartPermalink = buildCartPermalink(row);
      const checkoutLabel = row?.dataset.checkoutLabel || "SOLD OUT";

      setButtonUrl(primary, cartPermalink);
      setButtonText(primary, cartPermalink ? checkoutLabel : "SOLD OUT");

      if (secondary) {
        setButtonUrl(secondary, cartPermalink ? `${cartPermalink}?storefront=true` : "");
        setButtonText(secondary, "ADD TO CART");
      }

      if (sticky) {
        setButtonUrl(sticky, cartPermalink);

        if (stickyFull && stickyShort) {
          stickyFull.textContent = cartPermalink ? `CHECKOUT — ${row.dataset.packSize} / ${row.dataset.total}` : "SOLD OUT";
          stickyShort.textContent = cartPermalink ? `CHECKOUT — ${row.dataset.packSize} / ${row.dataset.total}` : "SOLD OUT";
        } else {
          sticky.textContent = cartPermalink ? `CHECKOUT — ${row.dataset.packSize} / ${row.dataset.total}` : "SOLD OUT";
        }
      }
    };

    const selectRow = (row, shouldUpdateUrl = true) => {
      if (!row || row.dataset.available !== "true") return;

      rows.forEach((candidate) => {
        const selected = candidate === row;
        candidate.classList.toggle("is-selected", selected);
        candidate.setAttribute("aria-checked", selected ? "true" : "false");
      });

      const selectedText = `${row.dataset.unitsLabel} / ${row.dataset.issue}`;

      if (selectedLine) selectedLine.textContent = selectedText;
      if (selectedUnits) selectedUnits.textContent = row.dataset.unitsLabel;
      if (selectedTotal) selectedTotal.textContent = row.dataset.total;
      if (selectedUnit) selectedUnit.textContent = row.dataset.unit;
      if (selectedSavings) selectedSavings.textContent = row.dataset.savingsShort;
      updateProductImage(row);
      updateButtons(row);

      if (stickyLine) {
        stickyLine.textContent = `${row.dataset.unitsLabel} / ${row.dataset.total} / FREE SHIPPING`;
      }

      if (shouldUpdateUrl) updateUrl(row);
    };

    const requestJson = async (url, body) => {
      const options = {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      };

      if (body) {
        options.body = JSON.stringify(body);
      }

      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`Cart request failed: ${response.status}`);
      }

      return response.json();
    };

    const getCart = async () => {
      const response = await fetch(cartJsonUrl, {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        throw new Error(`Cart read failed: ${response.status}`);
      }

      return response.json();
    };

    const addCanonicalVariant = (quantity) => requestJson(cartAddUrl, {
      items: [
        {
          id: canonicalVariantId,
          quantity,
        },
      ],
    });

    const removeCanonicalLines = async () => {
      const cart = await getCart();
      const matchingItems = (cart.items || []).filter((item) => (
        String(item.variant_id) === String(canonicalVariantId)
      ));

      for (const item of matchingItems) {
        await requestJson(cartChangeUrl, {
          id: item.key,
          quantity: 0,
        });
      }
    };

    const setBusy = (button, busy, label) => {
      if (!button) return;

      if (busy) {
        setActionText(button, label);
        button.setAttribute("aria-busy", "true");
        button.setAttribute("aria-disabled", "true");
      } else {
        button.removeAttribute("aria-busy");
        updateButtons(selectedRow());
      }
    };

    const cartReady = (row) => {
      if (canonicalVariantId && canonicalVariantReady && selectedQuantity(row)) return true;

      console.warn("Fluid Cache PDP requires a canonical single-unit Shopify variant ID before cart actions can run.");
      return false;
    };

    const handleCartAction = async (event, action) => {
      const row = selectedRow();
      const actionButton = event.currentTarget;

      event.preventDefault();

      if (!cartReady(row)) return;

      const quantity = selectedQuantity(row);
      setBusy(actionButton, true, action === "checkout" ? "CHECKOUT..." : "ADDING...");

      try {
        if (action === "checkout") {
          await requestJson(cartClearUrl);
          await addCanonicalVariant(quantity);
          window.location.href = checkoutUrl;
          return;
        }

        await removeCanonicalLines();
        await addCanonicalVariant(quantity);
        window.location.href = cartUrl;
      } catch (error) {
        console.warn("Fluid Cache cart update failed.", error);
        setBusy(actionButton, false);
      }
    };

    rows.forEach((row) => {
      row.addEventListener("click", () => selectRow(row));

      row.addEventListener("keydown", (event) => {
        const keys = ["ArrowDown", "ArrowRight", "ArrowUp", "ArrowLeft", "Home", "End", " ", "Enter"];
        if (!keys.includes(event.key)) return;

        event.preventDefault();

        if (event.key === " " || event.key === "Enter") {
          selectRow(row);
          return;
        }

        const candidates = availableRows();
        const currentIndex = candidates.indexOf(row);
        let nextIndex = currentIndex;

        if (event.key === "Home") nextIndex = 0;
        if (event.key === "End") nextIndex = candidates.length - 1;
        if (event.key === "ArrowDown" || event.key === "ArrowRight") {
          nextIndex = currentIndex >= candidates.length - 1 ? 0 : currentIndex + 1;
        }
        if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
          nextIndex = currentIndex <= 0 ? candidates.length - 1 : currentIndex - 1;
        }

        const nextRow = candidates[nextIndex];
        if (nextRow) {
          nextRow.focus();
          selectRow(nextRow);
        }
      });
    });

    [primary, sticky].forEach((button) => {
      button?.addEventListener("click", (event) => handleCartAction(event, "checkout"));
    });

    secondary?.addEventListener("click", (event) => handleCartAction(event, "add"));

    if (!canonicalVariantId) {
      console.warn("Fluid Cache PDP requires a canonical single-unit Shopify variant ID.");
      updateButtons(selectedRow());
      return;
    }

    const url = new URL(window.location.href);
    const urlQuantity = url.searchParams.get("quantity");
    const urlRow = rows.find((row) => (
      row.dataset.selectedUnits === urlQuantity &&
      row.dataset.available === "true"
    ));

    if (urlRow) {
      selectRow(urlRow, false);
    } else {
      updateButtons(selectedRow());
    }
  });
})();
